// src/components/VersionCheck.tsx
//
// Erkennt neue Netlify-Deployments und zeigt einen dezenten Banner,
// der den User zum Neuladen auffordert.
//
// Funktionsweise:
// 1. Beim App-Start: Lädt index.html und merkt sich die Script-Hashes
// 2. Alle 5 Minuten: Prüft ob sich die Script-Hashes geändert haben
// 3. Wenn ja: Zeigt Banner "Neue Version verfügbar" mit Reload-Button

import { useState, useEffect, useRef, useCallback } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 Minuten

/**
 * Extrahiert alle <script src="..."> URLs aus dem HTML-String.
 * Create React App generiert Dateinamen mit Content-Hashes (z.B. main.a1b2c3.js),
 * die sich bei jedem Build ändern.
 */
function extractScriptHashes(html: string): string[] {
  const matches = html.match(/<script[^>]+src="([^"]+)"/g) || [];
  return matches
    .map(tag => {
      const srcMatch = tag.match(/src="([^"]+)"/);
      return srcMatch ? srcMatch[1] : '';
    })
    .filter(Boolean)
    .sort();
}

export const VersionCheck: React.FC = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialHashes = useRef<string[] | null>(null);

  const checkForUpdate = useCallback(async () => {
    try {
      const response = await fetch('/', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!response.ok) return;

      const html = await response.text();
      const currentHashes = extractScriptHashes(html);

      if (initialHashes.current === null) {
        // Erster Aufruf: Referenz speichern
        initialHashes.current = currentHashes;
        return;
      }

      // Vergleich: Haben sich die Script-Hashes geändert?
      const changed = 
        currentHashes.length !== initialHashes.current.length ||
        currentHashes.some((hash, i) => hash !== initialHashes.current![i]);

      if (changed) {
        console.log('[VersionCheck] Neue App-Version erkannt');
        setUpdateAvailable(true);
      }
    } catch {
      // Netzwerkfehler ignorieren (offline, etc.)
    }
  }, []);

  useEffect(() => {
    // Initiale Referenz laden
    checkForUpdate();

    // Regelmäßig prüfen
    const interval = setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkForUpdate]);

  if (!updateAvailable) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      backgroundColor: '#1E40AF',
      color: 'white',
      padding: '12px 20px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      zIndex: 9999,
      animation: 'slideIn 0.3s ease-out'
    }}>
      <style>{`
        @keyframes slideIn {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <span>Eine neue Version ist verfügbar.</span>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '6px 14px',
          backgroundColor: 'white',
          color: '#1E40AF',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: '600',
          whiteSpace: 'nowrap'
        }}
      >
        Neu laden
      </button>
      <button
        onClick={() => setUpdateAvailable(false)}
        style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.7)',
          cursor: 'pointer',
          fontSize: '18px',
          padding: '0 4px',
          lineHeight: 1
        }}
        title="Später"
      >
        ×
      </button>
    </div>
  );
};

export default VersionCheck;
